/**
 * Ephemeris interpolation for the telemetry panel: Lagrange polynomial
 * interpolation of degree 5 over the six nearest samples, the same scheme the
 * globe uses (SampledPositionProperty with LagrangePolynomialApproximation),
 * so the panel and the entity agree. Positions are km in the samples' frame.
 */
import type { EphemerisSample } from '../types';

export interface Vector3 {
  x: number;
  y: number;
  z: number;
}

export const LAGRANGE_DEGREE = 5;

export function magnitude(v: Vector3): number {
  return Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
}

export function distance(a: Vector3, b: Vector3): number {
  return magnitude({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
}

/** Lagrange interpolation of (xs, ys) at x over all given points. */
export function lagrange(xs: readonly number[], ys: readonly number[], x: number): number {
  let sum = 0;
  for (let i = 0; i < xs.length; i++) {
    let basis = 1;
    for (let j = 0; j < xs.length; j++) {
      if (j !== i) basis *= (x - xs[j]) / (xs[i] - xs[j]);
    }
    sum += ys[i] * basis;
  }
  return sum;
}

/** Index of the last sample at or before t (binary search); -1 before the first. */
export function indexAtOrBefore(times: readonly number[], t: number): number {
  let lo = 0;
  let hi = times.length - 1;
  if (hi < 0 || t < times[0]) return -1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (times[mid] <= t) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/** Sorted sample times plus positionAt(t) with degree-5 Lagrange interpolation; null outside the span. */
export class EphemerisTrack {
  readonly times: number[];
  readonly samples: EphemerisSample[];
  readonly startMs: number;
  readonly endMs: number;

  constructor(samples: readonly EphemerisSample[]) {
    const valid = samples
      .map((s) => ({ s, t: new Date(s.t).getTime() }))
      .filter((e) => Number.isFinite(e.t) && Number.isFinite(e.s.x) && Number.isFinite(e.s.y) && Number.isFinite(e.s.z))
      .sort((a, b) => a.t - b.t);
    this.samples = valid.map((e) => e.s);
    this.times = valid.map((e) => e.t);
    this.startMs = this.times.length ? this.times[0] : NaN;
    this.endMs = this.times.length ? this.times[this.times.length - 1] : NaN;
  }

  get length(): number {
    return this.times.length;
  }

  positionAt(tMs: number): Vector3 | null {
    const n = this.times.length;
    if (n === 0 || tMs < this.startMs || tMs > this.endMs) return null;
    if (n === 1) return { x: this.samples[0].x, y: this.samples[0].y, z: this.samples[0].z };
    const points = Math.min(LAGRANGE_DEGREE + 1, n);
    const at = indexAtOrBefore(this.times, tMs);
    // Centre the window on the bracketing interval, clamped to the ends.
    let first = at - Math.floor((points - 1) / 2);
    if (first < 0) first = 0;
    if (first + points > n) first = n - points;
    const xs = new Array<number>(points);
    const px = new Array<number>(points);
    const py = new Array<number>(points);
    const pz = new Array<number>(points);
    for (let i = 0; i < points; i++) {
      const s = this.samples[first + i];
      xs[i] = this.times[first + i] - this.times[first];
      px[i] = s.x;
      py[i] = s.y;
      pz[i] = s.z;
    }
    const x = tMs - this.times[first];
    return { x: lagrange(xs, px, x), y: lagrange(xs, py, x), z: lagrange(xs, pz, x) };
  }
}
